/**
 * =============================================================================
 * ASSETWAVE - MAIN APPLICATION COMPONENT
 * =============================================================================
 * 
 * This is the root component of the Assetwave financial advisory website.
 * It sets up the React Router for client-side navigation and defines all
 * application routes.
 * 
 * @author Assetwave Development Team
 * @version 1.0.0
 * 
 * ROUTE STRUCTURE:
 * ----------------
 * /                          - Home page with all main sections
 * /about                     - About the advisor and company
 * /services/term-insurance   - Term insurance information
 * /services/health-insurance - Health insurance information
 * /services/life-insurance   - Life insurance information
 * /services/mutual-funds     - Mutual funds information
 * /services/sip              - SIP (Systematic Investment Plan) information
 * /services/general-insurance- General insurance information
 * /services/bonds            - Bonds investment information
 * /services/mf-loan          - Loan Against Mutual Funds information
 * /services/govt-schemes     - Government Schemes information
 * /privacy-policy            - Privacy policy page
 * /terms-conditions          - Terms and conditions page
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Page Components
import Home from './pages/Home';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';

// Service Pages
import TermInsurance from './pages/services/TermInsurance';
import HealthInsurance from './pages/services/HealthInsurance';
import LifeInsurance from './pages/services/LifeInsurance';
import MutualFunds from './pages/services/MutualFunds';
import SIP from './pages/services/SIP';
import GeneralInsurance from './pages/services/GeneralInsurance';
import Bonds from './pages/services/Bonds';
import MFLoan from './pages/services/MFLoan';
import GovtSchemes from './pages/services/GovtSchemes';

// Utility Components
import ScrollToTop from './components/ScrollToTop';

/**
 * Main Application Component
 * 
 * Sets up routing and global components that persist across all pages.
 * ScrollToTop ensures the page scrolls to top on route changes.
 */
function App(): JSX.Element {
  return (
    <Router>
      {/* Scroll to top on route change */}
      <ScrollToTop />
      
      {/* Application Routes */}
      <Routes>
        {/* Main Pages */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        
        {/* Service Pages */}
        <Route path="/services/term-insurance" element={<TermInsurance />} />
        <Route path="/services/health-insurance" element={<HealthInsurance />} />
        <Route path="/services/life-insurance" element={<LifeInsurance />} />
        <Route path="/services/mutual-funds" element={<MutualFunds />} />
        <Route path="/services/sip" element={<SIP />} />
        <Route path="/services/general-insurance" element={<GeneralInsurance />} />
        <Route path="/services/bonds" element={<Bonds />} />
        <Route path="/services/mf-loan" element={<MFLoan />} />
        <Route path="/services/govt-schemes" element={<GovtSchemes />} />
        
        {/* Legal Pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
      </Routes>
    </Router>
  );
}

export default App;
